import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X, Settings } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const STORAGE_KEY = "oo-cookie-consent-v1";

function readConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeConsent(prefs) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...prefs, ts: new Date().toISOString() })
    );
  } catch {
    /* ignore */
  }
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [prefs, setPrefs] = useState({
    necessary: true,
    analytics: true,
    marketing: false,
  });

  useEffect(() => {
    const existing = readConsent();
    if (!existing) {
      const t = setTimeout(() => setVisible(true), 700);
      return () => clearTimeout(t);
    }
  }, []);

  const acceptAll = () => {
    writeConsent({ necessary: true, analytics: true, marketing: true });
    setVisible(false);
  };
  const rejectAll = () => {
    writeConsent({ necessary: true, analytics: false, marketing: false });
    setVisible(false);
  };
  const saveChoices = () => {
    writeConsent(prefs);
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="cookie-banner"
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-0 left-0 right-0 z-[60] p-4 md:p-6"
        >
          <div className="mx-auto max-w-5xl glass rounded-sm shadow-[0_20px_60px_-20px_rgba(10,61,122,0.35)] overflow-hidden">
            <div className="p-6 md:p-8">
              <div className="flex flex-col md:flex-row md:items-start gap-6">
                <div className="flex-shrink-0 w-12 h-12 bg-[#0a3d7a] text-[#faf6f0] flex items-center justify-center rounded-sm">
                  <Cookie size={20} />
                </div>
                <div className="flex-1">
                  <p className="font-label text-[#f59e0b] mb-2">
                    Cookies & Privacy
                  </p>
                  <h3 className="font-display text-2xl md:text-3xl text-[#0a3d7a] leading-tight mb-3">
                    We use cookies to craft a better experience.
                  </h3>
                  <p className="font-body text-sm text-[#0a3d7a]/75 leading-relaxed">
                    Ocean Oasis uses cookies to personalise content, understand
                    how you use our site, and improve our services across our
                    three divisions. You can accept all, reject non-essential,
                    or customise your preferences. Read our{" "}
                    <Link
                      to="/cookies-policy"
                      data-testid="cookie-banner-policy-link"
                      className="underline decoration-[#f59e0b] underline-offset-2 hover:text-[#f59e0b]"
                    >
                      Cookies Policy
                    </Link>
                    .
                  </p>

                  {showCustomize && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      className="mt-5 space-y-3 border-t border-[rgba(10,61,122,0.15)] pt-5"
                    >
                      {[
                        {
                          key: "necessary",
                          label: "Strictly Necessary",
                          desc: "Required for the site to function. Always on.",
                          disabled: true,
                        },
                        {
                          key: "analytics",
                          label: "Analytics",
                          desc: "Helps us understand how visitors use our site.",
                        },
                        {
                          key: "marketing",
                          label: "Marketing",
                          desc: "Used to deliver relevant communications.",
                        },
                      ].map((row) => (
                        <label
                          key={row.key}
                          data-testid={`cookie-pref-${row.key}`}
                          className={`flex items-start gap-3 p-3 border border-[rgba(10,61,122,0.12)] ${
                            row.disabled
                              ? "bg-[#fdf9f2] cursor-not-allowed"
                              : "cursor-pointer hover:bg-[#fdf9f2]"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={prefs[row.key]}
                            disabled={row.disabled}
                            onChange={(e) =>
                              setPrefs((p) => ({
                                ...p,
                                [row.key]: e.target.checked,
                              }))
                            }
                            className="mt-1 accent-[#0a3d7a]"
                          />
                          <div className="flex-1">
                            <p className="font-body font-semibold text-sm text-[#0a3d7a]">
                              {row.label}
                            </p>
                            <p className="font-body text-xs text-[#0a3d7a]/65 mt-0.5">
                              {row.desc}
                            </p>
                          </div>
                        </label>
                      ))}
                    </motion.div>
                  )}

                  <div className="mt-6 flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={acceptAll}
                      data-testid="cookie-accept-button"
                      className="btn-primary"
                    >
                      Accept All
                    </button>
                    <button
                      type="button"
                      onClick={rejectAll}
                      data-testid="cookie-reject-button"
                      className="btn-outline"
                    >
                      Reject Non-Essential
                    </button>
                    {!showCustomize ? (
                      <button
                        type="button"
                        onClick={() => setShowCustomize(true)}
                        data-testid="cookie-customize-button"
                        className="inline-flex items-center gap-2 px-4 py-3 font-body text-xs uppercase tracking-[0.18em] text-[#0a3d7a]/70 hover:text-[#f59e0b]"
                      >
                        <Settings size={14} />
                        Customise
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={saveChoices}
                        data-testid="cookie-save-button"
                        className="btn-outline"
                      >
                        Save Choices
                      </button>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={rejectAll}
                  aria-label="Dismiss"
                  data-testid="cookie-dismiss"
                  className="absolute top-4 right-4 md:relative md:top-0 md:right-0 text-[#0a3d7a]/60 hover:text-[#0a3d7a] transition-colors"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
